import { Button, Icon, Modal, useTranslate } from "@tovi/ui";
import { type Bill, type BillBook, labelOf, releaseLabel } from "@/lib/bills.ts";

/**
 * The bills this till has open, and the way in to a new one.
 *
 * A till holds several at once — `08` § A step 7's "Order more / Pending /
 * Resume", and several take-away bags waiting at the counter at the same time.
 * A horizontal strip above the cart rather than `@tovi/ui`'s `Tabs`, which
 * wraps: a wrapping row here would push the cart column down, and § A is
 * explicit that the cart never moves.
 */
export function BillStrip({
  book,
  onSwitch,
  onNew,
}: {
  book: BillBook;
  onSwitch: (key: string) => void;
  onNew: () => void;
}) {
  const t = useTranslate();
  if (book.bills.length === 0) return null;

  return (
    <div
      role="tablist"
      aria-label={t("pos.bill.open")}
      className="flex items-center gap-1 overflow-x-auto border-b border-line bg-surface px-2 py-1.5"
    >
      {book.bills.map((bill) => (
        <button
          key={bill.key}
          type="button"
          role="tab"
          aria-selected={bill.key === book.activeKey}
          // A bag chip carries no text, so the tab has to say what it is.
          aria-label={
            bill.isHeld
              ? t("pos.bill.heldLabel", { name: labelOf(bill) ?? t("pos.bill.newTakeAway") })
              : bill.table
                ? undefined
                : t("pos.bill.newTakeAway")
          }
          onClick={() => onSwitch(bill.key)}
          // A fixed height and a border on both states: a bag chip carries no
          // text, and left to its content it stands 6px shorter than the table
          // chip beside it.
          className={`flex h-8 shrink-0 items-center gap-1.5 rounded-control border px-3 text-sm ${
            bill.key === book.activeKey
              ? "border-primary bg-primary font-semibold text-ink-on-primary"
              : "border-line text-ink-body"
          } ${
            // Parked bills read as parked without a second glyph: no icon in
            // the shared set means "on hold", and inventing one for the till
            // alone would be a primitive nobody else could use.
            bill.isHeld ? "border-dashed opacity-60" : ""
          }`}
        >
          <Icon name={bill.table ? "table" : "bag"} />
          {labelOf(bill)}
          <BillDot bill={bill} />
        </button>
      ))}
      <button
        type="button"
        onClick={onNew}
        aria-label={t("pos.bill.startTitle")}
        className="h-8 shrink-0 rounded-control border border-line px-3 text-sm text-ink-muted"
      >
        +
      </button>
    </div>
  );
}

/** Whether the server has heard of this bill yet. */
function BillDot({ bill }: { bill: Bill }) {
  const t = useTranslate();
  if (bill.status === "open") return null;

  const failed = bill.status === "failed";
  return (
    <span
      aria-label={failed ? t("pos.bill.failed") : t("pos.bill.notSent")}
      title={failed ? (bill.failure ?? t("pos.bill.failed")) : t("pos.bill.notSent")}
      className={`size-1.5 rounded-pill ${failed ? "bg-danger" : "bg-warning"}`}
    />
  );
}

/**
 * What stands where the menu's tiles would be, before a bill is open.
 *
 * Not a modal over the grid: a cashier answering "do you have the fish?" while
 * the party is still standing needs the menu searchable, and a dialog would
 * take that away. The tiles disable instead and this says why.
 */
export function StartBillPanel({
  onChooseTable,
  onTakeAway,
}: {
  onChooseTable: () => void;
  onTakeAway: () => void;
}) {
  const t = useTranslate();

  return (
    <div className="m-3 flex flex-col gap-3 rounded-card border border-line bg-surface-subtle p-4">
      <div>
        <h2 className="text-sm font-semibold text-ink-body">{t("pos.bill.startTitle")}</h2>
        <p className="mt-1 text-sm text-ink-muted">{t("pos.bill.startBody")}</p>
      </div>
      {/* Wraps, and stacks under a narrow till: the Khmer strings run about a
          third longer, and a button pushed off the edge is a till that cannot
          open a bill at all. */}
      <div className="flex flex-wrap gap-2">
        <Button variant="primary" onClick={onChooseTable}>
          {t("pos.bill.chooseTable")}
        </Button>
        <Button variant="secondary" onClick={onTakeAway}>
          {t("pos.bill.newTakeAway")}
        </Button>
      </div>
    </div>
  );
}

/** Letting an empty bill go: a table back to the floor, or a take-away away. */
export function ReleaseDialog({
  bill,
  onClose,
  onRelease,
}: {
  bill: Bill;
  onClose: () => void;
  onRelease: () => void;
}) {
  const t = useTranslate();
  const isTakeAway = bill.diningStyle === "take_away";

  return (
    <Modal open title={t(releaseLabel(bill.diningStyle))} onClose={onClose}>
      <div className="flex flex-col gap-4">
        <p className="text-sm text-ink-body">
          {isTakeAway
            ? t("pos.bill.discardBillConfirm")
            : t("pos.bill.clearTableConfirm", { table: bill.table?.name ?? "" })}
        </p>
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t("pos.bill.clearTableKeep")}
          </Button>
          <Button variant="primary" onClick={onRelease}>
            {t(isTakeAway ? "pos.bill.discard" : "pos.bill.release")}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
